<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useQuizStore, useUserStore } from '../../../../../stores';
import { UserRole } from '../../../../../types/user';
import { QuizAudioKey } from '../../../../../types/quiz';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../../../configs/text';
import { audioManager } from '../../../../../utils/audioManager';
import { ClassStudentInfo } from '../../../../../types/class';

import confetti from 'canvas-confetti';

import ButtonV2 from '../../../../General/ButtonV2.vue';
import RankFrameRender from '../../../../General/RankFrameRender.vue';
import LeaderBoard from './LeaderBoard.vue';
import QuizViewAnswers from './QuizViewAnswers.vue';
import Heading from '../../../../General/Heading.vue';
import TopScoreBg from '../../../../SVG/TopScoreBg.vue';
import SlideInElliptic from '../../../../Animation/SlideInElliptic.vue';

import quizTopViewBgm from '../../../../../assets/audios/quiz_top_view_bgm.mp3'
import quizConfettiAudio from '../../../../../assets/audios/quiz_confetti.mp3'

const enum leaderboardViewMode {
    FINISHING = 'finishing',
    TOP_VIEW = 'top_view',
    BOARD_VIEW = 'board_view',
    ANSWERS_VIEW = 'answers_view'
}

const { t } = useI18n()
const user = useUserStore()
const quizStore = useQuizStore()
const viewMode = ref<leaderboardViewMode>(leaderboardViewMode.FINISHING)
const top3Students = computed<ClassStudentInfo[]>(() => quizStore.leaderBoard.filter((student) => quizStore.studentTotalPoint(student)).slice(0, 3));
const visibleItems = ref<number[]>([]);
const loopingItems = ref<number[]>([]);
const fireworksCanvas = ref<HTMLCanvasElement | null>(null);
const currentStudentIndex = computed<number>(() => {
    return quizStore.leaderBoard.findIndex((student) => user.username === student.username) || 0
})

function handleAnimationLoop(index: number) {
    if (user.userRole !== UserRole.student) {
        loopingItems.value.push(index)
        if (index === 1) {
            setTimeout(() => {
                triggerFireworks()
            }, 700)
        }
    } else {
        triggerFireworks()
    }
}

function handleAnimationFinish(index: number) {
    visibleItems.value.push(index - 1)
}

// Function to trigger fireworks
function triggerFireworks() {
    if (!fireworksCanvas.value || !(fireworksCanvas.value instanceof HTMLCanvasElement)) {
        console.error('fireworksCanvas is not a valid HTMLCanvasElement');
        return;
    }

    const canvas = fireworksCanvas.value;
    const confettiInstance = confetti.create(canvas, { resize: true, useWorker: true });
    const duration = 20 * 1000; // Fireworks duration (20 seconds)
    const animationEnd = Date.now() + duration;

    const defaults = {
        origin: { y: 0.7 },
        gravity: 0.5,
    };

    const fire = (particleRatio: number, opts: Record<string, any>) => {
        confettiInstance({
            ...defaults,
            ...opts,
            particleCount: Math.floor(200 * particleRatio),
        });
    };
    // Fire initial bursts
    const initialBursts = [
        { particleRatio: 0.25, opts: { spread: 26, startVelocity: 55 } },
        { particleRatio: 0.2, opts: { spread: 60 } },
        { particleRatio: 0.35, opts: { spread: 100, decay: 0.91, scalar: 0.8 } },
        { particleRatio: 0.1, opts: { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 } },
        { particleRatio: 0.1, opts: { spread: 120, startVelocity: 45 } },
    ];

    initialBursts.forEach(({ particleRatio, opts }) => fire(particleRatio, opts));
    // Continuous fireworks effect
    const frame = () => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) return;

        confettiInstance({
            particleCount: 1,
            startVelocity: 0,
            ticks: Math.max(200, 500 * (timeLeft / duration)),
            origin: {
                x: Math.random(),
                y: Math.random() * 0.6 + 0.1, // Random y position with a slight offset
            },
            colors: [randomColor()],
            gravity: randomInRange(0.4, 0.6),
            scalar: randomInRange(0.4, 1),
            drift: randomInRange(-0.4, 0.4),
        });

        requestAnimationFrame(frame);
    };

    frame();
    audioManager.play(quizConfettiAudio, QuizAudioKey.SFX)
    setTimeout(() => {
        playBackgroundMusic()
    }, 3000)
}

function randomInRange(min: number, max: number): number {
    return Math.random() * (max - min) + min;
}

function randomColor(): string {
    const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffffff'];
    return colors[Math.floor(Math.random() * colors.length)];
}

function showTopView() {
    viewMode.value = leaderboardViewMode.TOP_VIEW
    visibleItems.value = [top3Students.value.length]
}
function playBackgroundMusic() {
    audioManager.play(quizTopViewBgm, QuizAudioKey.BGM, { loop: true, muted: quizStore.bgmVolumeState.muted, volume: quizStore.bgmVolumeState.volume })

}
onMounted(() => {
    quizStore.updateLeaderboard()
});

onBeforeUnmount(() => {
    audioManager.stop(QuizAudioKey.BGM)
    audioManager.stop(QuizAudioKey.SFX)
    audioManager.stop('tada')
    audioManager.stop('tadasub')
})
</script>
<template>

    <SlideInElliptic @after-leave="showTopView" v-if="viewMode === leaderboardViewMode.FINISHING">
        <p>
            {{ user.userRole === UserRole.student ? t(TEXT.finish_quiz_student) : t(TEXT.finish_quiz_teacher) }}
        </p>
    </SlideInElliptic>

    <Transition mode="out-in" enter-active-class="transform transition duration-200 absolute" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transform transition duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0" v-if="viewMode !== leaderboardViewMode.FINISHING">

        <QuizViewAnswers @back="viewMode = leaderboardViewMode.BOARD_VIEW" v-if="viewMode === leaderboardViewMode.ANSWERS_VIEW"></QuizViewAnswers>

        <div class="flex items-center w-full h-full relative overflow-hidden" v-else>
            <!-- Canvas for fireworks -->
            <canvas ref="fireworksCanvas" class="absolute top-0 left-0 w-full h-full pointer-events-none"></canvas>
            <div class="w-full h-full flex flex-col gap-3 items-center overflow-hidden ">

                <template v-if="viewMode === leaderboardViewMode.TOP_VIEW">
                    <TopScoreBg :title="user.userRole === UserRole.student ? t(TEXT.your_result) : t(TEXT.quiz_top_score)" />
                    <div class="flex flex-col items-center justify-start md:justify-normal md:items-stretch md:flex-row w-full h-full overflow-auto md:overflow-hidden grow p-3 " v-if="top3Students.length">
                        <template v-if="user.userRole === UserRole.teacher">
                            <!-- #3 -->
                            <div class="w-1/3 order-3 md:order-3">
                                <div v-if="top3Students[2] && visibleItems.includes(3)" class="flex flex-col h-full justify-center items-center animate-bounce-in">
                                    <Transition enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s] delay-500" enter-from-class="tracking-tighter opacity-0" enter-to-class=" opacity-1">
                                        <p class="text-xl text-center font-semibold text-blue-950 dark:text-white " v-if="loopingItems.includes(3)">
                                            {{ top3Students[2].full_name }}
                                        </p>
                                    </Transition>

                                    <RankFrameRender class="transistion" :placement="3" :assets="top3Students[2].avatar" width="auto" height="100%" @loop="handleAnimationLoop(3)" @finish="handleAnimationFinish(3)" />

                                    <Transition enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s]" enter-from-class="tracking-tighter opacity-0" enter-to-class="opacity-1">
                                        <p class="text-xl text-center font-semibold text-blue-950 dark:text-white" v-if="loopingItems.includes(3)">
                                            {{ `${quizStore.studentTotalPoint(top3Students[2])} /
                                            ${quizStore.quiz?.totalPoint} ${t(TEXT.pts)}` }}
                                        </p>
                                    </Transition>
                                </div>
                            </div>
                            <!-- #1 -->
                            <div class="w-1/3 order-1 md:order-2">
                                <div v-if="top3Students[0] && visibleItems.includes(1)" class="flex flex-col h-full justify-center items-center animate-bounce-in">
                                    <Transition enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s] delay-500" enter-from-class="tracking-tighter opacity-0" enter-to-class="opacity-1">
                                        <p class="text-xl text-center font-semibold text-blue-950 dark:text-white" v-if="loopingItems.includes(1)">
                                            {{ top3Students[0].full_name }}
                                        </p>
                                    </Transition>

                                    <RankFrameRender class="transistion" :placement="1" :assets="top3Students[0].avatar" width="auto" height="100%" @loop="handleAnimationLoop(1)" />

                                    <Transition enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s]" enter-from-class="tracking-tighter opacity-0" enter-to-class="opacity-1">
                                        <p class="text-xl text-center font-semibold text-blue-950 dark:text-white" v-if="loopingItems.includes(1)">
                                            {{ `${quizStore.studentTotalPoint(top3Students[0])} /
                                            ${quizStore.quiz?.totalPoint} ${t(TEXT.pts)}` }}
                                        </p>
                                    </Transition>
                                </div>
                            </div>
                            <!-- #2 -->
                            <div class="w-1/3 order-2 md:order-1">
                                <div v-if="top3Students[1] && visibleItems.includes(2)" class="flex flex-col h-full justify-center items-center animate-bounce-in">
                                    <Transition enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s] delay-500" enter-from-class="tracking-tighter opacity-0" enter-to-class="opacity-1">
                                        <p class="text-xl text-center font-semibold text-blue-950 dark:text-white" v-if="loopingItems.includes(2)">
                                            {{ top3Students[1].full_name }}
                                        </p>
                                    </Transition>

                                    <RankFrameRender class="transistion" :placement="2" :assets="top3Students[1].avatar" width="auto" height="100%" @loop="handleAnimationLoop(2)" @finish="handleAnimationFinish(2)" />

                                    <Transition enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s]" enter-from-class="tracking-tighter opacity-0" enter-to-class="opacity-1">
                                        <p class="text-xl text-center font-semibold text-blue-950 dark:text-white" v-if="loopingItems.includes(2)">
                                            {{ `${quizStore.studentTotalPoint(top3Students[1])} /
                                            ${quizStore.quiz?.totalPoint} ${t(TEXT.pts)}` }}
                                        </p>
                                    </Transition>
                                </div>
                            </div>
                        </template>
                        <div class="w-full h-full flex flex-col justify-center items-center animate-bounce-in" v-if="user.userRole === UserRole.student">

                            <Transition appear enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s] delay-[2s]" enter-from-class="tracking-tighter opacity-0" enter-to-class="opacity-1">
                                <p class="text-xl text-center font-semibold text-blue-950 dark:text-white " :class="{ 'animate-tracking-in-expand-40': currentStudentIndex + 1 === 1 }">
                                    {{ quizStore.leaderBoard[currentStudentIndex].full_name }}
                                </p>
                            </Transition>

                            <RankFrameRender :placement="quizStore.studentTotalPoint(quizStore.leaderBoard[currentStudentIndex]) > 0 ? currentStudentIndex + 1 : 0" :assets="quizStore.leaderBoard[currentStudentIndex].avatar"
                                @loop="handleAnimationLoop(quizStore.studentTotalPoint(quizStore.leaderBoard[currentStudentIndex]) > 0 ? currentStudentIndex + 1 : 0)" width="auto" height="70%" />

                            <Transition enter-active-class="ease-[cubic-bezier(0.215, 0.610, 0.355, 1.000)] duration-[0.7s] delay-[1s]" enter-from-class="tracking-tighter opacity-0" enter-to-class="opacity-1">
                                <p v-if="quizStore.studentTotalPoint(quizStore.leaderBoard[currentStudentIndex]) > 0" class="text-xl text-center font-semibold text-blue-950 dark:text-white animate-tracking-in-expand-24"
                                    :class="{ 'animate-tracking-in-expand-40': currentStudentIndex + 1 === 1 }">
                                    {{ `${quizStore.studentTotalPoint(quizStore.leaderBoard[currentStudentIndex])} /
                                    ${quizStore.quiz?.totalPoint} ${t(TEXT.pts)}` }}
                                </p>
                            </Transition>

                            <p v-if="quizStore.studentTotalPoint(quizStore.leaderBoard[currentStudentIndex]) > 0" class="text-xl text-center font-semibold text-blue-950 dark:text-white animate-tracking-in-expand-24 first-letter:uppercase"
                                :class="{ 'animate-tracking-in-expand-40': currentStudentIndex + 1 === 1 }"
                                v-html="currentStudentIndex <= 2 ? t(TEXT.final_result_quiz_text_1, { 'student_ranking': currentStudentIndex + 1 }) : t(TEXT.final_result_quiz_text_2, { 'student_ranking': currentStudentIndex + 1 })">
                            </p>

                            <p v-else class="text-xl text-center font-semibold text-blue-950 dark:text-white animate-tracking-in-expand-24 first-letter:uppercase" :class="{ 'animate-tracking-in-expand-40': currentStudentIndex + 1 === 1 }"
                                v-html="t(TEXT.final_result_quiz_text_2)">
                            </p>
                        </div>
                    </div>
                    <div v-else class="grow mt-10 flex justify-center items-center">
                        <Heading class="text-primary">{{ t(TEXT.no_ranking) }}</Heading>
                    </div>

                    <div class="flex w-full justify-center gap-3 p-3">
                        <ButtonV2 class="text-nowrap px-5" color="primary" animation @click="viewMode = leaderboardViewMode.BOARD_VIEW">
                            {{ t(TEXT.view_ranking_btn) }}
                        </ButtonV2>
                        <ButtonV2 class="text-nowrap px-5" color="success" animation @click="viewMode = leaderboardViewMode.ANSWERS_VIEW">
                            {{ t(TEXT.view_answer_btn) }}
                        </ButtonV2>
                    </div>
                </template>
                <div class="flex-1 w-full flex flex-col gap-3 items-center overflow-hidden px-20 py-10" v-if="viewMode === leaderboardViewMode.BOARD_VIEW">
                    <div class="flex-1 w-full overflow-hidden pt-5">
                        <LeaderBoard />
                    </div>
                    <div class="flex w-full justify-center">
                        <ButtonV2 color="success" animation @click="viewMode = leaderboardViewMode.ANSWERS_VIEW">
                            {{ t(TEXT.view_answer_btn) }}
                        </ButtonV2>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>